import { itx } from "../../../src/translationItx/itx";
import { useOnChangeWatcher } from "../../../src/models/useOnChangeWatcher";
import { Plus, Star } from "lucide-react";
import type { C1_EquipmentStandard, C1_HouseType } from "../types/C1_ModelTypes";

export type C1_ProjectFormValue = {
  projectName: string;
  sampleIndex: number;
  houseType: C1_HouseType;
  buildingWidth: number;
  buildingLength: number;
  floorDistance: number;
  equipmentStandard: C1_EquipmentStandard;
};

type ProjectFormProps = {
  value: C1_ProjectFormValue;
  onChange: (value: C1_ProjectFormValue) => void;
  samples: ReadonlyArray<{ label: string }>;
  idPrefix: string;
  showTemplate?: boolean;
  showEquipment?: boolean;
  onSubmit?: () => void;
};

/* Haustyp-Auswahl vorerst deaktiviert, da sie aktuell keine Bedeutung hat.
const houseTypes: Array<{
  value: C1_HouseType;
  label: string;
  width: number;
  length: number;
}> = [
  { value: "detachedHouse", get label() { return itx("c1.ui.Freistehendes_Einfamilienhaus", "Freistehendes Einfamilienhaus"); }, width: 12, length: 12 },
  { value: "semiDetachedHouseLeft", get label() { return itx("c1.ui.Doppelhaushalfte_links", "Doppelhaushälfte (links)"); }, width: 12, length: 6 },
  { value: "semiDetachedHouseRight", get label() { return itx("c1.ui.Doppelhaushalfte_rechts", "Doppelhaushälfte (rechts)"); }, width: 12, length: 6 },
  { value: "terracedHouse", get label() { return itx("c1.ui.Reihenhaus", "Reihenhaus"); }, width: 12, length: 5 },
  { value: "apartment", get label() { return itx("c1.ui.Apartment", "Apartment"); }, width: 6, length: 8 },
];
*/

export function C1_ProjectForm({
  value,
  onChange,
  samples,
  idPrefix,
  showTemplate = true,
  showEquipment = true,
  onSubmit,
}: ProjectFormProps) {
  useOnChangeWatcher(state.settings);
  const update = (changes: Partial<C1_ProjectFormValue>) => onChange({ ...value, ...changes });

  return (
    <div className="C1_projectCreateRow">
      <label className="C1_projectNameField" htmlFor={`${idPrefix}-projectName`}>
        {(itx("coverPage.projectName", "Projektname") + ":")} <input
          id={`${idPrefix}-projectName`}
          value={value.projectName}
          onChange={(event) => update({ projectName: event.target.value })}
          placeholder={itx("ExportGroupAdressesDialog.projectname", "Projektname")}
        />
      </label>
      <br />

      <div className="C1_projectBasicsRow">
        {showTemplate && (
          <label className="C1_templateField" htmlFor={`${idPrefix}-sampleSelect`}>
            {(itx("components.Template", "Vorlage") + ":")} <select
              id={`${idPrefix}-sampleSelect`}
              value={value.sampleIndex}
              onChange={(event) => update({ sampleIndex: Number(event.target.value) })}
            >
              {samples.map((sample, index) => (
                <option key={sample.label} value={index}>
                  {sample.label}
                </option>
              ))}
              <option value={-1}>{itx("c1.ui.Leeres_Projekt_keine_Vorlage", "Leeres Projekt (keine Vorlage)")}</option>
            </select>
          </label>
        )}

        <div className="C1_dimensionRow">
          <label>
            {itx("c1.ui.Ungefahre_Aussenmasse", "Ungefähre Außenmaße:")} <span className="C1_dimensionInputs">
              <input
                aria-label={itx("c1.ui.Breite", "Breite")}
                type="number"
                min="0.1"
                step="1"
                value={value.buildingWidth}
                onChange={(event) => update({ buildingWidth: Number(event.target.value) })}
              />
              ×
              <input
                aria-label={itx("c1.ui.Lange", "Länge")}
                type="number"
                min="0.1"
                step="1"
                value={value.buildingLength}
                onChange={(event) => update({ buildingLength: Number(event.target.value) })}
              />
              {itx("c1.ui.m_Breite_Lange", "m (Breite × Länge)")} </span>
          </label>
          <label>
            {itx("c1.ui.floorSpacingLabel", "Etagenabstand:")} <span className="C1_dimensionInputs">
              <input
                aria-label={itx("c1.ui.Etagenabstand", "Etagenabstand")}
                type="number"
                min="0.1"
                step="0.1"
                value={value.floorDistance}
                onChange={(event) => update({ floorDistance: Number(event.target.value) })}
              />
              m
            </span>
          </label>
        </div>
      </div>
      <br />

      <div className="C1_houseEquipmentRow">
        {/* Haustyp-Auswahl vorerst deaktiviert.
        <div className="C1_houseType">
          <label htmlFor={`${idPrefix}-houseType`}>{itx("c1.ui.Typ_des_Hauses", "Typ des Hauses:")}</label>
          <select
            id={`${idPrefix}-houseType`}
            value={value.houseType}
            onChange={(event) => {
              const selected = houseTypes.find((option) => option.value === event.target.value);
              if (!selected) return;
              update({
                houseType: selected.value,
                buildingWidth: selected.width,
                buildingLength: selected.length,
              });
            }}
          >
            {houseTypes.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        */}

        {showEquipment && (
          <div className="C1_equipmentStandard">
            <span className="C1_equipmentStandardLabel">{itx("c1.ui.Ausstattung", "Ausstattung:")}</span>
            <div className="C1_equipmentStandardOptions">
              {([3, 4, 5] as const).map((stars) => (
                <label key={stars} className={value.equipmentStandard === stars ? "C1_selected" : ""}>
                  <input
                    type="radio"
                    name={`${idPrefix}-equipmentStandard`}
                    value={stars}
                    checked={value.equipmentStandard === stars}
                    onChange={() => update({ equipmentStandard: stars })}
                  />
                  <span className="C1_equipmentStandardNumber" aria-hidden="true">
                    {stars}
                  </span>
                  <span aria-label={`${stars} Sterne`}>
                    {Array.from({ length: stars }, (_, index) => (
                      <Star key={index} size={20} fill="currentColor" aria-hidden="true" />
                    ))}
                  </span>
                </label>
              ))}
            </div>
          </div>
        )}
      </div>

      {onSubmit && (
        <>
          <br />
          <button type="button" className="C1_primary" onClick={onSubmit}>
            <Plus size={18} />  {itx("c1.ui.Anlegen", "Anlegen")} </button>
        </>
      )}
    </div>
  );
}
